import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, CalendarDays, FileText, ExternalLink, Send, Printer, CheckCircle2,
} from "lucide-react";
import { LeadflowForm } from "@/components/LeadflowForm";
import { PageHero } from "@/components/PageHero";
import { img } from "@/lib/images";
import { site } from "@/lib/site";
import { canonicalLinks, routeMeta } from "@/lib/seo";

export const Route = createFileRoute("/tax-center")({
  head: () => ({
    meta: [
      ...routeMeta("/tax-center"),
      { title: "Tax Center: Refunds, Deadlines & Organizer | CCOB" },
      { name: "description", content: "Track your federal and state refund, view 2026 tax deadlines, and complete your online tax organizer — all in one place." },
      { property: "og:title", content: "Tax Center — CCOB And Company PA" },
      { property: "og:description", content: "Refund tracker, deadlines, and online organizer." },
    ],
    links: canonicalLinks("/tax-center"),
  }),
  component: TaxCenterPage,
});

type TabId = "refund" | "due-dates" | "organizer";

const TABS: { id: TabId; label: string; icon: typeof Search; blurb: string }[] = [
  { id: "refund", label: "Track Your Refund", icon: Search, blurb: "Federal & state refund status" },
  { id: "due-dates", label: "Tax Due Dates", icon: CalendarDays, blurb: "Key 2026 deadlines" },
  { id: "organizer", label: "Online Organizer", icon: FileText, blurb: "Start your return" },
];

function TaxCenterPage() {
  const [tab, setTab] = useState<TabId>("refund");
  return (
    <>
      <PageHero
        eyebrow="Tax Center"
        title="Tools, Deadlines & Refund Tracking"
        subtitle="Everything you need between filings — check refunds, mark your calendar, and start your organizer."
        image={img.heroTax}
      />
      <section className="py-16 md:py-20">
        <div className="container-x">
          {/* Primary 3-tier toggle */}
          <div className="mb-12 md:mb-16">
            <div className="text-center mb-5 md:mb-7">
              <div className="text-gold uppercase tracking-[0.3em] text-xs">Choose a tool</div>
              <h2 className="font-display text-2xl md:text-3xl text-primary mt-2">What do you need today?</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-0 md:rounded-2xl md:border md:border-border md:bg-card md:p-2 md:shadow-sm">
              {TABS.map((t, i) => {
                const active = t.id === tab;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    aria-pressed={active}
                    className={`relative flex items-center gap-4 md:gap-3 lg:gap-4 text-left px-5 md:px-6 py-5 md:py-6 rounded-2xl md:rounded-xl border md:border-0 transition-all ${
                      active
                        ? "bg-primary text-primary-foreground border-primary shadow-lg md:shadow-md scale-[1.01]"
                        : "bg-card text-foreground border-border hover:border-gold hover:bg-secondary/40"
                    }`}
                  >
                    <div
                      className={`shrink-0 h-12 w-12 md:h-14 md:w-14 rounded-xl grid place-items-center transition-colors ${
                        active ? "bg-primary-foreground/15 text-primary-foreground" : "bg-secondary text-primary"
                      }`}
                    >
                      <t.icon className="h-6 w-6 md:h-7 md:w-7" strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`text-[10px] md:text-[11px] uppercase tracking-[0.25em] ${active ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                        Step {i + 1}
                      </div>
                      <div className="font-display text-lg md:text-xl leading-tight mt-1">
                        {t.label}
                      </div>
                      <div className={`text-xs md:text-sm mt-1 ${active ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                        {t.blurb}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
            >
              {tab === "refund" && <RefundPanel />}
              {tab === "due-dates" && <DueDatesPanel />}
              {tab === "organizer" && <OrganizerPanel />}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>
    </>
  );
}

/* ─────────── REFUND ─────────── */

const STATES: { name: string; url: string }[] = [
  { name: "Alabama", url: "https://myalabamataxes.alabama.gov/_/" },
  { name: "Arizona", url: "https://aztaxes.gov/Home/CheckRefund" },
  { name: "Arkansas", url: "https://atap.arkansas.gov/_/" },
  { name: "California", url: "https://webapp.ftb.ca.gov/refund/login" },
  { name: "Colorado", url: "https://tax.colorado.gov/refund" },
  { name: "Connecticut", url: "https://drsindtax.ct.gov/AUT/welcomeindividual.aspx" },
  { name: "Delaware", url: "https://revenue.delaware.gov/check-refund-status/" },
  { name: "Georgia", url: "https://gtc.dor.ga.gov/_/" },
  { name: "Hawaii", url: "https://tax.ehawaii.gov/hoihoi/refund.html" },
  { name: "Idaho", url: "https://tax.idaho.gov/taxes/income-tax/individual-income/refund/" },
  { name: "Illinois", url: "https://mytax.illinois.gov/_/" },
  { name: "Indiana", url: "https://intime.dor.in.gov/eServices/_/" },
  { name: "Iowa", url: "https://tax.iowa.gov/wheres-my-refund" },
  { name: "Kansas", url: "https://www.ksrevenue.gov/refundstatus.html" },
  { name: "Kentucky", url: "https://refund.ky.gov/" },
  { name: "Louisiana", url: "https://latap.revenue.louisiana.gov/_/" },
  { name: "Maine", url: "https://portal.maine.gov/refundstatus/refund" },
  { name: "Maryland", url: "https://interactive.marylandtaxes.gov/INDIV/refundstatus/home.aspx" },
  { name: "Massachusetts", url: "https://mtc.dor.state.ma.us/mtc/_/" },
  { name: "Michigan", url: "https://www.michigan.gov/taxes/iit/refund" },
  { name: "Minnesota", url: "https://www.revenue.state.mn.us/wheres-my-refund" },
  { name: "Mississippi", url: "https://tap.dor.ms.gov/_/" },
  { name: "Missouri", url: "https://mytax.mo.gov/rptp/portal/home/return-tracker" },
  { name: "Montana", url: "https://tap.dor.mt.gov/_/" },
  { name: "Nebraska", url: "https://ndr-refundstatus.ne.gov/refundstatus/public/search.faces" },
  { name: "New Jersey", url: "https://www.nj.gov/treasury/taxation/checkrefundstatus.shtml" },
  { name: "New Mexico", url: "https://tap.state.nm.us/Tap/_/" },
  { name: "New York", url: "https://www.tax.ny.gov/pit/file/refund.htm" },
  { name: "North Carolina", url: "https://eservices.dor.nc.gov/wheresmyrefund/" },
  { name: "North Dakota", url: "https://apps.nd.gov/tax/tap/_/" },
  { name: "Ohio", url: "https://tax.ohio.gov/individual/refund-status" },
  { name: "Oklahoma", url: "https://oktap.tax.ok.gov/oktap/web/_/" },
  { name: "Oregon", url: "https://revenueonline.dor.oregon.gov/tap/_/" },
  { name: "Pennsylvania", url: "https://mypath.pa.gov/_/" },
  { name: "Rhode Island", url: "https://www.ri.gov/taxation/refund/" },
  { name: "South Carolina", url: "https://dor.sc.gov/tax/refund-call" },
  { name: "Utah", url: "https://tap.utah.gov/TaxExpress/_/" },
  { name: "Vermont", url: "https://myvtax.vermont.gov/_/" },
  { name: "Virginia", url: "https://www.individual.tax.virginia.gov/VTOL/IndRefundStatus.seam" },
  { name: "Washington DC", url: "https://mytax.dc.gov/_/" },
  { name: "West Virginia", url: "https://mytaxes.wvtax.gov/_/" },
  { name: "Wisconsin", url: "https://tap.revenue.wi.gov/RefundStatus/_/" },
];

function RefundPanel() {
  const [state, setState] = useState("");
  const selected = STATES.find((s) => s.name === state);
  return (
    <div className="grid lg:grid-cols-2 gap-8 max-w-5xl">
      <div className="bg-card border border-border rounded-2xl p-8">
        <div className="text-gold uppercase tracking-[0.25em] text-xs">Federal Refund</div>
        <h2 className="font-display text-2xl text-primary mt-2">IRS — Where's My Refund?</h2>
        <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
          Status updates within 24 hours after the IRS receives your e-filed return, or about 4 weeks after a paper return is mailed.
        </p>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
          You'll need your Social Security Number, filing status, and exact refund amount.
        </p>
        <a href="https://www.irs.gov/refunds" target="_blank" rel="noreferrer" className="btn-primary mt-7">
          Check Federal Refund <ExternalLink className="h-4 w-4" />
        </a>
      </div>

      <div className="bg-card border border-border rounded-2xl p-8">
        <div className="text-gold uppercase tracking-[0.25em] text-xs">State Refund</div>
        <h2 className="font-display text-2xl text-primary mt-2">Select Your State</h2>
        <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
          Florida has no state income tax — if you filed in another state, choose it below.
        </p>
        <div className="mt-6">
          <label className="text-xs uppercase tracking-widest text-muted-foreground">State</label>
          <select
            value={state}
            onChange={(e) => setState(e.target.value)}
            className="mt-2 w-full rounded-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:border-gold"
          >
            <option value="">— Select a state —</option>
            {STATES.map((s) => (
              <option key={s.name} value={s.name}>{s.name}</option>
            ))}
          </select>
        </div>
        {selected ? (
          <a href={selected.url} target="_blank" rel="noreferrer" className="btn-gold mt-6">
            Check {selected.name} Refund <ExternalLink className="h-4 w-4" />
          </a>
        ) : (
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Search className="h-4 w-4" /> Pick a state to continue
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────── DUE DATES ─────────── */

type Group = "Individual" | "Business" | "Payroll" | "Estimated Tax";
const groups: Group[] = ["Individual", "Business", "Payroll", "Estimated Tax"];
const dates: { group: Group; date: string; title: string; desc: string }[] = [
  { group: "Individual", date: "Jan 31, 2026", title: "Form W-2 / 1099 to recipients", desc: "Employers and payers must furnish wage and information returns to recipients." },
  { group: "Individual", date: "Apr 15, 2026", title: "Form 1040 — Individual Tax Return", desc: "Federal individual income tax return due. Last day to file an extension (Form 4868) and pay any tax owed." },
  { group: "Individual", date: "Jun 15, 2026", title: "Expats / Citizens Abroad", desc: "U.S. citizens or residents living and working outside the U.S. — automatic extended filing deadline." },
  { group: "Individual", date: "Oct 15, 2026", title: "Extended 1040 Deadline", desc: "Final deadline if you filed Form 4868 in April." },
  { group: "Business", date: "Mar 16, 2026", title: "S-Corp (1120-S) & Partnership (1065)", desc: "Calendar-year S-Corp and partnership returns due (or file Form 7004 for a 6-month extension)." },
  { group: "Business", date: "Apr 15, 2026", title: "C-Corp (1120) Returns", desc: "Calendar-year C-Corp federal return due." },
  { group: "Business", date: "Sep 15, 2026", title: "Extended 1120-S / 1065 Deadline", desc: "Final deadline for extended S-Corp and partnership returns." },
  { group: "Business", date: "Oct 15, 2026", title: "Extended 1120 Deadline", desc: "Final deadline for extended C-Corp returns." },
  { group: "Payroll", date: "Jan 31, 2026", title: "Form 941 (Q4 2025) & Form 940", desc: "Quarterly federal payroll tax return for Q4 2025 and annual FUTA return." },
  { group: "Payroll", date: "Apr 30, 2026", title: "Form 941 (Q1 2026)", desc: "Quarterly federal payroll tax return for Q1 2026." },
  { group: "Payroll", date: "Jul 31, 2026", title: "Form 941 (Q2 2026)", desc: "Quarterly federal payroll tax return for Q2 2026." },
  { group: "Payroll", date: "Oct 31, 2026", title: "Form 941 (Q3 2026)", desc: "Quarterly federal payroll tax return for Q3 2026." },
  { group: "Estimated Tax", date: "Apr 15, 2026", title: "Q1 Estimated Tax Payment", desc: "First quarterly estimated payment for the 2026 tax year (Form 1040-ES)." },
  { group: "Estimated Tax", date: "Jun 15, 2026", title: "Q2 Estimated Tax Payment", desc: "Second quarterly estimated payment for the 2026 tax year." },
  { group: "Estimated Tax", date: "Sep 15, 2026", title: "Q3 Estimated Tax Payment", desc: "Third quarterly estimated payment for the 2026 tax year." },
  { group: "Estimated Tax", date: "Jan 15, 2027", title: "Q4 Estimated Tax Payment", desc: "Final quarterly estimated payment for the 2026 tax year." },
];

function DueDatesPanel() {
  const [active, setActive] = useState<Group>("Individual");
  const filtered = dates.filter((d) => d.group === active);
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-8">
        {groups.map((g) => (
          <button
            key={g}
            onClick={() => setActive(g)}
            className={`px-5 py-2.5 rounded-full text-xs uppercase tracking-widest border transition ${
              g === active
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card text-foreground border-border hover:border-gold"
            }`}
          >
            {g}
          </button>
        ))}
      </div>
      <ul key={active} className="divide-y divide-border border border-border rounded-2xl bg-card overflow-hidden">
        {filtered.map((d) => (
          <li key={d.date + d.title} className="p-6 md:p-7 flex flex-col md:flex-row gap-5">
            <div className="md:w-48 shrink-0 flex md:block items-center gap-3">
              <div className="h-9 w-9 rounded-full grid place-items-center bg-primary text-primary-foreground md:mb-3">
                <CalendarDays className="h-4 w-4" strokeWidth={1.6} />
              </div>
              <div className="font-display text-lg text-primary">{d.date}</div>
            </div>
            <div className="flex-1">
              <div className="font-display text-lg text-primary">{d.title}</div>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{d.desc}</p>
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-6 text-xs text-muted-foreground max-w-2xl">
        Dates reflect the federal calendar for the 2026 tax year and may shift if a deadline falls on a weekend or holiday. State deadlines may differ.
      </p>
    </div>
  );
}

/* ─────────── ORGANIZER ─────────── */

function OrganizerPanel() {
  return (
    <div className="max-w-3xl bg-card border border-border rounded-2xl p-8 md:p-10">
      <LeadflowForm src="https://leadflow.puulse.ai/ccobandcompany/online-tax-organizer" />
    </div>
  );
}
